import { lazy, Suspense } from 'react'
import { createBrowserRouter } from 'react-router-dom'

import { AuthGuard } from './components/AuthGuard'
import {
  MovieDetailShim,
  SeriesDetailShim,
  SeriesPlayerShim,
  VodPlayerShim,
} from './components/DeepLinkRedirect'
import { LoadingScreen } from './components/LoadingScreen'
import { PageFade } from './components/PageFade'
import { ProfileGuard } from './components/ProfileGuard'
import { resetDesiredColumnOnPathChange } from './components/rowNav'
import { SwipeGuard } from './components/SwipeGuard'
import { RootRoute } from './RootRoute' // extraído pra ser testável sem createBrowserRouter
import { deriveRouterBasenameFromWindow } from './router-basename'
import { AccountGateScreen } from './screens/AccountGateScreen'
import { OnboardingModeScreen } from './screens/OnboardingModeScreen'

const ProviderCodeScreen = lazy(() =>
  import('./screens/ProviderCodeScreen').then((m) => ({ default: m.ProviderCodeScreen })),
)
const DirectLoginScreen = lazy(() =>
  import('./screens/DirectLoginScreen').then((m) => ({ default: m.DirectLoginScreen })),
)
const ManualSourceScreen = lazy(() =>
  import('./screens/ManualSourceScreen').then((m) => ({ default: m.ManualSourceScreen })),
)
const LoginScreen = lazy(() =>
  import('./screens/LoginScreen').then((m) => ({ default: m.LoginScreen })),
)
const AccountLoginScreen = lazy(() =>
  import('./screens/AccountLoginScreen').then((m) => ({ default: m.AccountLoginScreen })),
)
const AccountSignupScreen = lazy(() =>
  import('./screens/AccountSignupScreen').then((m) => ({ default: m.AccountSignupScreen })),
)
const HomeScreen = lazy(() =>
  import('./screens/HomeScreen').then((m) => ({ default: m.HomeScreen })),
)
const LiveScreen = lazy(() =>
  import('./screens/LiveScreen').then((m) => ({ default: m.LiveScreen })),
)
const MoviesScreen = lazy(() =>
  import('./screens/MoviesScreen').then((m) => ({ default: m.MoviesScreen })),
)
// MovieDetailsScreen e VodPlayerScreen NÃO são mais telas de ROTA — viraram
// OVERLAYS (lazy dentro do DetailOverlayHost). As rotas `/movies/:id` e
// `/vod-player/:id` são shims de deep-link (abrem a pilha + redirecionam pro
// catálogo). Ver docs/plan/tv-ficha-overlay.md.
const SeriesScreen = lazy(() =>
  import('./screens/SeriesScreen').then((m) => ({ default: m.SeriesScreen })),
)
// SeriesDetailsScreen e SeriesPlayerScreen NÃO são mais telas de ROTA — viraram
// OVERLAYS (lazy dentro do DetailOverlayHost, P2). As rotas `/series/:id` e
// `/series-player/:seriesId/:episodeId` são shims de deep-link (abrem a pilha +
// redirecionam pro catálogo de séries). Ver docs/plan/tv-ficha-overlay.md.
const PlayerScreen = lazy(() =>
  import('./screens/PlayerScreen').then((m) => ({ default: m.PlayerScreen })),
)
const SettingsScreen = lazy(() =>
  import('./screens/SettingsScreen').then((m) => ({ default: m.SettingsScreen })),
)
const ProfilesScreen = lazy(() =>
  import('./screens/ProfilesScreen').then((m) => ({ default: m.ProfilesScreen })),
)
const SearchScreen = lazy(() =>
  import('./screens/SearchScreen').then((m) => ({ default: m.SearchScreen })),
)
const EpgScreen = lazy(() =>
  import('./screens/EpgScreen').then((m) => ({ default: m.EpgScreen })),
)
const ReplayScreen = lazy(() =>
  import('./screens/ReplayScreen').then((m) => ({ default: m.ReplayScreen })),
)
const CatchupPlayerScreen = lazy(() =>
  import('./screens/CatchupPlayerScreen').then((m) => ({ default: m.CatchupPlayerScreen })),
)
// D.3 — "Ver tudo" de rails curados. 3 rotas (movies/series/home) mapeiam
// pro mesmo componente parametrizado por `kind` interno. Home usa mixed
// (filme + série juntos) — card identifica o tipo via badge no poster.
const CuratedFullMovies = lazy(() =>
  import('./screens/CuratedFullScreen').then((m) => ({ default: m.CuratedFullMovies })),
)
const CuratedFullSeries = lazy(() =>
  import('./screens/CuratedFullScreen').then((m) => ({ default: m.CuratedFullSeries })),
)
const CuratedFullHome = lazy(() =>
  import('./screens/CuratedFullScreen').then((m) => ({ default: m.CuratedFullHome })),
)

/** Fallback exibido enquanto código lazy carrega. Antes era `...` solto;
 *  agora reusa `LoadingScreen` (spinner + texto) pra ter identidade
 *  visual consistente com loadings internos das telas. */
function RouteFallback() {
  return <LoadingScreen />
}

function withSuspense(node: React.ReactNode) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <PageFade>{node}</PageFade>
    </Suspense>
  )
}

/**
 * Rotas do INTERIOR do app. Passam por DUAS fronteiras, aninhadas nesta ordem
 * porque são perguntas diferentes:
 *
 *  1. `SwipeGuard` (fora) — "QUEM é este usuário?". É a fronteira de ENTRADA:
 *     sem conta Swipe não se entra, ponto. Decide no render.
 *  2. `AuthGuard` (dentro) — "a FONTE deste aparelho ainda vale?". Reage a
 *     `isAuthed` cair no meio da sessão (logout, 401, 403 account_expired) e a
 *     `accountExpiresAt` vencer. É reativo, vive em effect.
 *
 * POR QUE O SWIPEGUARD TAMBÉM AQUI (P0 do review de ed623cbf): fechar as telas
 * que CUNHAM a sessão não fecha o INTERIOR, porque `isAuthed` é PERSISTIDO —
 * está no `partialize` do `persist({name:'iptv:session'})` (store.ts). Quem já
 * tinha `isAuthed:true` + `iptv:tokens` no localStorage de
 * `app.swipetvplayer.com` digitava `/home` e recebia o app inteiro, PRA SEMPRE,
 * sem nunca ver o gate — e essa população é exatamente a que a porta anônima
 * criou. `isAuthed` nunca foi identidade: é "há fonte Xtream resolvida".
 *
 * NÃO é carve-out da base instalada: o desenho aprovado já a retém no gate
 * (RootRoute → `AccountGateScreen` variante `link`, que só oferece SAIR — não
 * existe "continuar sem vincular"). O deep link pro interior era fuga de uma
 * restrição já desenhada; aqui o interior passa a mandar pro mesmo lugar que
 * `/` já mandava.
 *
 * Públicas DE VERDADE (`/`, `/account/*`, `/settings`) seguem com
 * `withSuspense` puro.
 */
function withAuth(node: React.ReactNode) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <SwipeGuard>
        <AuthGuard>
          {/* 3ª fronteira (10/10/2026): com conta Swipe, o interior exige perfil ativo.
              Sem ela, o voltar do navegador / deep link entrava em `pf:_main`, que não
              sincroniza. Ver components/ProfileGuard.tsx. */}
          <ProfileGuard>
            <PageFade>{node}</PageFade>
          </ProfileGuard>
        </AuthGuard>
      </SwipeGuard>
    </Suspense>
  )
}

/** A tela de PERFIS: as mesmas fronteiras do interior MENOS a de perfil — é ela que
 *  escolhe o perfil (com a de perfil, entrar nela sem perfil seria um laço). */
function withAuthNoProfile(node: React.ReactNode) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <SwipeGuard>
        <AuthGuard>
          <PageFade>{node}</PageFade>
        </AuthGuard>
      </SwipeGuard>
    </Suspense>
  )
}

/** Rotas de CONFIG DE FONTE: exigem CONTA SWIPE (`hasSwipeSession()`), que é a
 *  fronteira de entrada do app — e não `isAuthed`, que significa só "há fonte
 *  Xtream resolvida" e é justamente o que estas telas cunham. Sem conta o
 *  SwipeGuard manda pra `/`, e o RootRoute entrega ao gate (variante `link`
 *  pra base instalada: RECONDUZ, não desloga). Ver components/SwipeGuard.tsx. */
function withSwipe(node: React.ReactNode) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <SwipeGuard>
        <PageFade>{node}</PageFade>
      </SwipeGuard>
    </Suspense>
  )
}

const routes = [
  // PÚBLICAS DE VERDADE — e a lista é curta de propósito: `/` (o gate),
  // `/account/*` (como se OBTÉM uma conta) e `/settings` (idioma/Sobre antes de
  // ter conta). Guardar qualquer uma delas fecharia o laço em cima do usuário.
  { path: '/', element: <RootRoute /> },
  // GATE DE CONTA OBRIGATÓRIA (I5+I6) — porta única de entrada. RootRoute já
  // renderiza inline; esta rota existe pra navegação direta (ex: "Começar" do
  // slider) e pra deep-link. AccountGateScreen decide entrada vs vínculo.
  { path: '/account/gate', element: withSuspense(<AccountGateScreen />) },
  // CONFIG INTERNA DE FONTE — fora do boot (I5). Reconfigurar/escolher fonte
  // PÓS-conta: `withSwipe` é o que torna isso VERDADE em vez de intenção. Elas
  // eram públicas, e no web (app.swipetvplayer.com, MESMO bundle, com barra de
  // endereço) digitar `/code` pulava o gate e entrava no app sem conta nenhuma.
  // NÃO morrem: com conta seguem alcançáveis como "configurar fonte da minha
  // conta" (plano A3), inclusive como destino do RootRoute caso 3.
  { path: '/onboarding/mode', element: withSwipe(<OnboardingModeScreen />) },
  { path: '/code', element: withSwipe(<ProviderCodeScreen />) },
  // `/direct` = caminho de DEVICE (POST /v1/auth/direct): provider efêmero preso
  // ao aparelho, NÃO sincroniza. `/source/manual` = caminho de CONTA (PUT
  // preference, sourceType manual): a mesma tela do ponto de vista do usuário,
  // mas a fonte segue a conta entre aparelhos. Desde a decisão do dono (14/08) o
  // caminho de conta é o oficial, e é pra ele que o seletor e as Ajustes apontam.
  //
  // `/login` e `/source/manual` entram no MESMO guard, e isso não é zelo: a
  // lista das telas a fechar sai do INVARIANTE ("alcança `setAuth()` sem
  // conta?"), não das que foram reportadas. `/login` chama `api.login()` +
  // `setAuth()` direto (LoginScreen), e o `login-memory` pré-preenche as
  // credenciais — pra quem já tem `providerCode` persistido era UM Enter até o
  // app, sem passar por `/code`. Fechar só `/code` deixaria essa porta aberta.
  { path: '/direct', element: withSwipe(<DirectLoginScreen />) },
  { path: '/source/manual', element: withSwipe(<ManualSourceScreen />) },
  { path: '/login', element: withSwipe(<LoginScreen />) },
  // Conta cloud Swipe TV (Fase 4A.7-C) — telas internas de reconfiguração de
  // conta via QR. O gate de boot é a AccountGateScreen acima.
  { path: '/account/login', element: withSuspense(<AccountLoginScreen />) },
  { path: '/account/signup', element: withSuspense(<AccountSignupScreen />) },
  // INTERIOR — conta Swipe (SwipeGuard) + fonte válida (AuthGuard). Ver withAuth.
  { path: '/home', element: withAuth(<HomeScreen />) },
  { path: '/home/curated/:railId', element: withAuth(<CuratedFullHome />) },
  { path: '/live', element: withAuth(<LiveScreen />) },
  { path: '/movies', element: withAuth(<MoviesScreen />) },
  // D.3: "Ver tudo" do rail curado precisa vir ANTES de /movies/:id
  // pra react-router não casar `:id = 'curated'`.
  { path: '/movies/curated/:railId', element: withAuth(<CuratedFullMovies />) },
  // Shims de deep-link: abrem a ficha-overlay (e o player) e redirecionam pra
  // /movies com replace, estabelecendo a origem por baixo.
  { path: '/movies/:id', element: withAuth(<MovieDetailShim />) },
  { path: '/vod-player/:id', element: withAuth(<VodPlayerShim />) },
  { path: '/series', element: withAuth(<SeriesScreen />) },
  // D.3: mesmo padrão de /movies/curated/:railId — antes de /series/:id.
  { path: '/series/curated/:railId', element: withAuth(<CuratedFullSeries />) },
  // Shims de deep-link: abrem a ficha-overlay (e o player) de série e redirecionam
  // pra /series com replace, estabelecendo a origem por baixo.
  { path: '/series/:id', element: withAuth(<SeriesDetailShim />) },
  { path: '/series-player/:seriesId/:episodeId', element: withAuth(<SeriesPlayerShim />) },
  { path: '/player/:channelId', element: withAuth(<PlayerScreen />) },
  { path: '/search', element: withAuth(<SearchScreen />) },
  { path: '/epg', element: withAuth(<EpgScreen />) },
  { path: '/replay', element: withAuth(<ReplayScreen />) },
  { path: '/catchup/:channelId', element: withAuth(<CatchupPlayerScreen />) },
  { path: '/profiles', element: withAuthNoProfile(<ProfilesScreen />) },
  // Settings é PÚBLICA — user pode acessar pré-login pra ajustar idioma,
  // aparência, ver "Sobre" e (futuro) "Conta cloud" via QR code. Seções
  // que dependem de auth (Reprodução, Canais, Meus dados, Logout, debug
  // tier override) são escondidas pela própria SettingsScreen quando
  // `!isAuthed`. Sem isso, botão Settings na Welcome caía em loop com
  // AuthGuard redirecionando pra `/`.
  { path: '/settings', element: withSuspense(<SettingsScreen />) },
]

// `basename` derivado do runtime (ver router-basename.ts):
//  - web (http/https): '/' — as URLs continuam `/home` etc, WEB não regride.
//  - empacotado (file:// webOS/Tizen): o diretório do index.html, pra rota `/`
//    do app casar com `.../com.swipetv.tv/` e a navegação interna operar relativa
//    a ele. Derivado do location real — NUNCA hardcode do path de instalação.
// Vale pro router INTEIRO (rota inicial + navegações via History API), não só a
// primeira rota.
export const router = createBrowserRouter(routes, {
  basename: deriveRouterBasenameFromWindow(),
})

// Memória de COLUNA dos trilhos (rowNav) morre ao trocar de tela.
//
// Assinar o router — e não pendurar num unmount de componente — é o que cobre
// TODAS as saídas: push, replace e POP (voltar), inclusive voltar pra mesma
// tela por outro caminho. As rotas aqui são planas (não há layout envolvendo as
// telas) e os elementos são lazy, então a ordem de unmount/mount entre a tela
// velha e a nova não é determinística — um unmount de wrapper deixaria buraco.
// Ver o porquê da blindagem em components/rowNav.ts.
//
// `subscribe` só dispara em ATUALIZAÇÃO de estado (não no boot), que é o
// suficiente: no boot não há coluna escolhida.
router.subscribe((state) => {
  resetDesiredColumnOnPathChange(state.location.pathname)
})
